import { useSignal, useSignalEffect } from '@preact/signals-react';
import { toast } from 'react-toastify';
import PresentationModal from '../../organisms/PresentationModal';
import TextEdit from '../../molecules/TextEdit';
import { ErrorField } from '../../../types/ErrorField';
import { getErrorMessage } from '../../../utils/formErrorUtils';
import { fundingApi, Platform } from '../../../services/fundingApi';

// Componente autocontido (estado próprio, validação, onClose/onSuccess) no
// mesmo espírito de src/components/pages/communication/contact/ContactModalForm.tsx
// - ver docs/specs/funding/platform/PlatformModalForm.ui.md para a decisão de
// design (usa o organism PresentationModal em vez de painel lateral, porque
// PlatformListPage usa layout "default", sem ThreeColumnLayout).
interface PlatformModalFormProps {
    show: boolean;
    platform?: Platform | null;
    onClose: () => void;
    onSuccess: () => void;
}

type Form = {
    name: string;
    cetipAccountLca: string;
    cetipAccountCdb: string;
    cetipAccountDi: string;
};

const EMPTY_FORM: Form = { name: '', cetipAccountLca: '', cetipAccountCdb: '', cetipAccountDi: '' };

export default function PlatformModalForm({ show, platform, onClose, onSuccess }: PlatformModalFormProps) {
    const isEditing = !!platform;
    const isSending = useSignal(false);
    const errorFields = useSignal<ErrorField[]>([]);
    const form = useSignal<Form>(EMPTY_FORM);

    // Reidrata o form sempre que a modal é reaberta (nova plataforma para
    // editar, ou reaberta em modo criação) - evita manter valores da
    // edição/criação anterior.
    useSignalEffect(() => {
        if (!show) return;
        form.value = platform
            ? {
                name: platform.name,
                cetipAccountLca: platform.cetipAccountLca || '',
                cetipAccountCdb: platform.cetipAccountCdb || '',
                cetipAccountDi: platform.cetipAccountDi || '',
            }
            : { ...EMPTY_FORM };
        errorFields.value = [];
    });

    const updateField = (field: keyof Form, value: string) => {
        form.value = { ...form.value, [field]: value };
        errorFields.value = errorFields.value.filter((f) => f.fieldName !== field);
    };

    // Contrato exige pelo menos uma conta CETIP (LCA, CDB ou DB_DI) - nenhuma
    // é individualmente obrigatória, já que algumas plataformas só emitem um
    // dos produtos (ver docs/api-contract.md secao Plataformas). DB_DI é a
    // conta da contraparte (ex: SICOOB, ITAU UNIBANCO), não de uma corretora.
    const validate = (): boolean => {
        const errors: ErrorField[] = [];
        if (!form.value.name.trim()) errors.push({ fieldName: 'name', message: 'Nome é obrigatório' });
        if (!form.value.cetipAccountLca.trim() && !form.value.cetipAccountCdb.trim() && !form.value.cetipAccountDi.trim()) {
            errors.push({ fieldName: 'cetipAccountLca', message: 'Informe pelo menos uma conta CETIP (LCA, CDB ou DB DI)' });
        }
        errorFields.value = errors;
        return errors.length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        isSending.value = true;
        const snapshot = { ...form.value };
        const lca = snapshot.cetipAccountLca.trim() || null;
        const cdb = snapshot.cetipAccountCdb.trim() || null;
        const di = snapshot.cetipAccountDi.trim() || null;

        const result = isEditing
            ? await fundingApi.updatePlatform(platform!.id, {
                name: snapshot.name,
                cetip_account_lca: lca,
                cetip_account_cdb: cdb,
                cetip_account_di: di,
                clear_cetip_account_lca: lca === null,
                clear_cetip_account_cdb: cdb === null,
                clear_cetip_account_di: di === null,
            })
            : await fundingApi.createPlatform(snapshot.name, lca, cdb, di);

        isSending.value = false;

        if (!result.ok) {
            toast.error(result.message);
            errorFields.value = result.errors.length > 0 ? result.errors : errorFields.value;
            return;
        }

        toast.success(isEditing ? 'Plataforma atualizada.' : 'Plataforma cadastrada.');
        onSuccess();
        onClose();
    };

    const footer = (
        <div className="flex gap-2">
            <button
                onClick={onClose}
                disabled={isSending.value}
                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100 dark:border-slate-600 dark:text-white dark:hover:bg-slate-700"
            >
                Cancelar
            </button>
            <button
                onClick={handleSubmit}
                disabled={isSending.value}
                className="px-4 py-2 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 dark:bg-teal-700 dark:hover:bg-teal-800"
            >
                {isSending.value ? 'Salvando...' : 'Salvar'}
            </button>
        </div>
    );

    return (
        <PresentationModal
            id="platform-modal-form"
            show={show}
            onClose={onClose}
            header={isEditing ? 'Editar Plataforma' : 'Nova Plataforma'}
            footer={footer}
        >
            <div className="p-6 space-y-4 w-[420px]">
                <TextEdit
                    label="Nome"
                    value={form.value.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    required
                    errorMessage={getErrorMessage(errorFields.value, 'name')}
                />
                <TextEdit
                    label="Conta CETIP (LCA)"
                    value={form.value.cetipAccountLca}
                    onChange={(e) => updateField('cetipAccountLca', e.target.value)}
                    errorMessage={getErrorMessage(errorFields.value, 'cetipAccountLca')}
                    helperText="Deixe em branco se a plataforma não emite LCA"
                />
                <TextEdit
                    label="Conta CETIP (CDB)"
                    value={form.value.cetipAccountCdb}
                    onChange={(e) => updateField('cetipAccountCdb', e.target.value)}
                    helperText="Deixe em branco se a plataforma não emite CDB, ou se usa a mesma conta do LCA"
                />
                <TextEdit
                    label="Conta CETIP (DB DI)"
                    value={form.value.cetipAccountDi}
                    onChange={(e) => updateField('cetipAccountDi', e.target.value)}
                    helperText="Conta da contraparte de DB_DI (ex: SICOOB, ITAU UNIBANCO) - deixe em branco se não for contraparte de DI"
                />
            </div>
        </PresentationModal>
    );
}
